import React from 'react';

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * Design System Badge Component
 * 
 * Variants with semantic meaning:
 * - Default: Neutral information
 * - Secondary: Less prominent information
 * - Success: Positive status
 * - Warning: Caution status
 * - Error: Negative status
 * - Info: Informational status
 */
export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'md',
  className = '',
}) => {
  const baseStyles = `
    inline-flex items-center justify-center font-medium rounded-full
    whitespace-nowrap transition-all duration-200 ease-out
  `;

  const sizeStyles = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-xs',
    lg: 'px-3 py-1.5 text-sm',
  };

  const variantStyles = {
    default: `
      bg-surface-tertiary text-text-primary
      border border-border-light
    `,
    secondary: `
      bg-surface-secondary text-text-secondary
      border border-border-medium
    `,
    success: `
      bg-green-100 text-green-800
      border border-green-200
    `,
    warning: `
      bg-yellow-100 text-yellow-800
      border border-yellow-200
    `,
    error: `
      bg-red-100 text-red-800
      border border-red-200
    `,
    info: `
      bg-blue-100 text-blue-800
      border border-blue-200
    `,
  };

  const combinedClassName = `
    ${baseStyles}
    ${sizeStyles[size]}
    ${variantStyles[variant]}
    ${className}
  `.trim();

  return (
    <span className={combinedClassName}>
      {children}
    </span>
  );
};